import clsx from 'clsx'
import { Redirect } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ScrollView, View } from 'react-native'

import { AnimatedParallax } from '@/src/elements/animated-parallax-list/AnimatedParallaxList'
import { BasicHeader } from '@/src/elements/header/BasicHeader'
import { Image } from '@/src/elements/image'
import { Message } from '@/src/elements/message'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { AccessibilityHiddenTitle } from '@/src/features/accessibility/ui/components/AccessibilityHiddenTitle'
import { CommunitiesCategoryTagImage } from '@/src/features/community/ui/components/CommunitiesCategory/CommunitiesCategoryTagImage'
import { ResponsiveDisplay } from '@/src/general/components/ResponsiveDisplay'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants/TrackingSource.enum'
import { useRoutes } from '@/src/general/utils/routes'

import { useIntentionThemeQuery } from '../../../services/useIntentionTheme'
import { IntentionsList } from '../IntentionsScreen/components/IntentionsList'
import { IntentionsPagesCommons } from './components/IntentionsPagesCommon'

type TIntentionsThematicScreenProps = {
  landingPageSlug: string
}

export const IntentionThemeScreen = ({ landingPageSlug }: TIntentionsThematicScreenProps): ReactNode => {
  const { sm, xm, md, lg, xl } = useDimensions()
  const { data, isPending, isSuccess, isError, error } = useIntentionThemeQuery(landingPageSlug)
  const { locale } = useIntl()
  const localizedData = data ? data[locale] : null

  const { routes } = useRoutes()

  const { initializeRef } = useAccessibilityContext()

  const getContentPosition = () => {
    if (xl || lg) {
      return { top: '40%' }
    } else if (xm || md || sm) {
      return { right: 0, top: '30%' }
    }
    return undefined
  }

  const ImageComponent = () => (
    <Image
      source={{ uri: data?.picture ? getImageUrl(data.picture) : '' }}
      className="h-full w-auto rounded-none lg:max-h-full"
      contentFit="cover"
      contentPosition={getContentPosition()}
      placeholder={{ blurhash: 'LmLg9W-oNGt7~Cs.ofWC4:RkfRR*', isAnimated: true }}
    />
  )

  if (error?.name === 'NotFound') {
    return <Redirect href="not-found" />
  }

  if (localizedData == null && isSuccess) {
    return <Redirect href={routes['prayer-space']()} />
  }

  if (landingPageSlug !== localizedData?.slug && localizedData?.slug) {
    return <Redirect href={routes.intentions(localizedData?.slug)} />
  }

  const Body = () => (
    <View className="p-4 pt-0 xm:gap-6 xm:px-0">
      {!isPending && localizedData !== null && (
        <>
          <AccessibilityHiddenTitle
            ref={(ref) => {
              initializeRef(ref, 'main')
            }}
          >
            <FormattedMessage id="app.accessibility.intentions-theme" />
          </AccessibilityHiddenTitle>
          <IntentionsPagesCommons
            showWatchman={false}
            theme={landingPageSlug}
            trackingSource={TRACKING_SOURCE.INTENTIONS_THEMATIC_PAGE}
          />
        </>
      )}
      {isError && (
        <Message colors="danger" className="mx-auto w-[500px]">
          <FormattedMessage id="common.error.unknown" />
        </Message>
      )}
      <View className="mx-auto w-full max-w-[500px]">
        <IntentionsList
          showTitle={false}
          landingPageSlug={landingPageSlug}
          trackingSource={TRACKING_SOURCE.INTENTIONS_THEMATIC_PAGE}
        />
      </View>
    </View>
  )

  return (
    <>
      <View testID={TEST_ID.INTENTIONS.SCREEN.THEME_SCREEN} className="flex flex-1">
        <ResponsiveDisplay size="sm" behavior="minWidth">
          <ScrollView className="w-full">
            <View className="z-10">
              <BasicHeader goBackTo={routes['prayer-space']()} />
            </View>
            <CommunitiesCategoryTagImage
              ImageComponent={ImageComponent}
              customTagName={{ text: localizedData?.title || '', className: '' }}
            />
            <Spacer y={10} />
            <Body />
          </ScrollView>
        </ResponsiveDisplay>
        <ResponsiveDisplay size="xs" behavior="maxWidth">
          <AnimatedParallax.List
            ImageComponent={ImageComponent}
            ListHeaderComponent={() => (
              <AnimatedParallax.Header
                ImageComponent={ImageComponent}
                CustomHeader={<BasicHeader goBackTo={routes['prayer-space']()} />}
                content={
                  <Typography
                    size="xl"
                    black
                    className={clsx('absolute bottom-0 left-0 right-0 mx-4 mb-10 bg-transparent text-left text-white')}
                  >
                    {localizedData?.title || ''}
                  </Typography>
                }
                imageUri={data?.picture ? getImageUrl(data.picture) : ''}
              />
            )}
            renderItem={() => <Body />}
            data={[{}]}
            imageUri={data?.picture ? getImageUrl(data.picture) : ''}
          />
        </ResponsiveDisplay>
      </View>
    </>
  )
}
